import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { fromEvent } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { useAuth0 } from '@auth0/auth0-react';
import { LOGIN_ACTIVITY_KEY, LOGOUT_TIMEOUT_MINUTES, LOGOUT_TIMESTAMP } from '../constants';
import { useLogoutActivityMutation } from '../features/activity/api/activityApi';
import { setLoggedInStatus } from '../features/auth/slices/authSlice';

export const useAutoLogout = () => {
    const { isAuthenticated, logout } = useAuth0();
    const [trackLogoutActivity] = useLogoutActivityMutation();
    const timerRef = useRef<any>(null);
    const dispatch = useDispatch();

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    };

    const userLogout = () => {
        localStorage.removeItem(LOGOUT_TIMESTAMP);
        localStorage.removeItem(LOGIN_ACTIVITY_KEY);
        trackLogoutActivity();
        dispatch(setLoggedInStatus(false));

        logout({
            logoutParams: {
                returnTo: `${window.location.origin}/logout`,
            },
        });
    };

    const userActivityTracker = () => {
        localStorage.setItem(LOGOUT_TIMESTAMP, Date.now().toString());
        clearTimer();
        timerRef.current = setTimeout(() => {
            userLogout();
        }, LOGOUT_TIMEOUT_MINUTES * 60 * 1000);
    };

    const checkInitialTimestamp = () => {
        const currentTime = Date.now();
        const lastActivity = localStorage.getItem(LOGOUT_TIMESTAMP);

        if (lastActivity) {
            const lastActivityTime = parseInt(lastActivity, 10);

            if (isNaN(lastActivityTime)) {
                localStorage.setItem(LOGOUT_TIMESTAMP, currentTime.toString());
                userActivityTracker();
            } else {
                const timeDifference = currentTime - lastActivityTime;
                if (timeDifference > LOGOUT_TIMEOUT_MINUTES * 60 * 1000) {
                    userLogout();
                } else {
                    userActivityTracker();
                }
            }
        } else {
            localStorage.setItem(LOGOUT_TIMESTAMP, currentTime.toString());
        }
    };

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        checkInitialTimestamp();

        const mouseMove$ = fromEvent(document.getElementById('root') as HTMLElement, 'mousemove').pipe(
            throttleTime(1000)
        );

        const subscription$ = mouseMove$.subscribe(userActivityTracker);

        return () => {
            clearTimer();
            subscription$.unsubscribe();
        };
    }, [isAuthenticated]);

    return {
        userLogout,
    };
};
